/** @file Settings › API tokens: one token per agent (its name is the actor), one-time secret, client setup. */
import type { Translate } from '@pose2d/i18n/types';
import type { SettingsApi } from '../../api/createSettingsApi';
import { errorText } from '../../shell/i18n/errorText';
import { ClientSetup } from './ClientSetup';
import { TokenCreateForm } from './TokenCreateForm';
import { TokenItem } from './TokenItem';
import { useTokens } from './useTokens';

/** Props of TokensSection. */
export interface TokensSectionProps {
  t: Translate;
  client: Pick<SettingsApi, 'listTokens' | 'createToken' | 'revokeToken'>;
  origin: string;
  copy: (text: string) => Promise<void>;
}

/** The secret panel stays until Done; afterwards the secret is gone from the page. */
export function TokensSection({ t, client, origin, copy }: TokensSectionProps) {
  const { tokens, created, failure, busy, create, revoke, dismissCreated } = useTokens(client);
  return (
    <section className="shell-card settings-section" aria-labelledby="settings-tokens">
      <h2 id="settings-tokens">{t('tokens.title')}</h2>
      <p className="shell-hint">{t('tokens.hint')}</p>
      <TokenCreateForm t={t} busy={busy} onCreate={create} />
      {created === null ? null : (
        <div className="token-created" role="status">
          <ClientSetup t={t} origin={origin} name={created.token.name} secret={created.secret} copy={copy} />
          <button type="button" onClick={dismissCreated}>{t('tokens.done')}</button>
        </div>
      )}
      {failure === null ? null : <p role="alert" className="shell-error">{errorText(t, failure.thrown)}</p>}
      {tokens?.length === 0 ? <p className="shell-hint">{t('tokens.empty')}</p> : null}
      <ul className="token-list">
        {(tokens ?? []).map((token) => <TokenItem key={token.id} t={t} token={token} origin={origin} copy={copy} busy={busy} onRevoke={revoke} />)}
      </ul>
    </section>
  );
}
